import { CSSProperties } from "react";
import { BookingType } from "../enums/BookingScope";

/**
 * Colore personalizzato dei blocchi prenotazione nel calendario del
 * gestionale. Se il gestore non sceglie nulla resta il colore per tipo
 * (amichevole / torneo / lezione), gestito dalle classi CSS del calendario.
 */

/** Colori suggeriti (hex minuscoli): usati per i quadratini del selettore. */
export const BOOKING_PRESET_COLORS: readonly string[] = Object.freeze([
  "#f25d0b", // arancio Get Move
  "#2563eb", // blu
  "#0d9488", // verde acqua
  "#16a34a", // verde
  "#ca8a04", // giallo scuro
  "#dc2626", // rosso
  "#db2777", // rosa
  "#7c3aed", // viola
  "#0891b2", // ciano
  "#4b5563", // grigio
  "#78350f", // marrone
  "#0f172a", // blu notte
]);

/**
 * Preset per il `ColorPicker` di antd. ATTENZIONE: antd muta l'array
 * `colors` che riceve (sostituisce le stringhe con i propri oggetti colore),
 * quindi va sempre passata una copia fresca, mai una costante condivisa.
 */
export const buildAntdColorPresets = (): { label: string; colors: string[] }[] => [
  { label: "Colori suggeriti", colors: [...BOOKING_PRESET_COLORS] },
];

/** Colore di default per tipo, usato dalla legenda e come anteprima. */
export const BOOKING_TYPE_COLORS: Record<BookingType, string> = {
  [BookingType.Friendly]: "#1a1fcc",
  [BookingType.Tournament]: "#e0002e",
  [BookingType.Training]: "#00916e",
};

/** Colore di default delle prenotazioni con tipologia personalizzata. */
export const BOOKING_CUSTOM_TYPE_COLOR = "#475569";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export const isValidHexColor = (value?: unknown): value is string =>
  typeof value === "string" && HEX_RE.test(value);

/** Nero o bianco a seconda della luminanza del colore di sfondo (WCAG). */
export const readableTextColor = (hex: string): string => {
  if (!isValidHexColor(hex)) return "#ffffff";
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.45 ? "#111827" : "#ffffff";
};

/** Schiarisce o scurisce un hex di `amount` (−1..1) per il gradiente. */
const shade = (hex: string, amount: number): string => {
  const channel = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16);
    const next =
      amount >= 0 ? c + (255 - c) * amount : c + c * amount;
    return Math.round(Math.min(255, Math.max(0, next)))
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
};

/**
 * Stile inline del blocco quando la prenotazione ha un colore personalizzato:
 * il colore scelto è lo sfondo pieno del blocco (niente sfumatura), con il
 * testo bianco o nero a seconda della luminanza. `undefined` se non c'è un
 * colore valido (restano le classi per tipo).
 */
export const getBookingBlockStyle = (
  color?: string | null
): CSSProperties | undefined => {
  if (!isValidHexColor(color)) return undefined;
  return {
    background: color,
    backgroundImage: "none",
    color: readableTextColor(color),
    border: "none",
    // Ombra nella tinta del colore scelto, per l'hover.
    ["--bk-block-shadow" as string]: `${shade(color, -0.25)}55`,
  };
};
